import { useCallback, useState } from 'react';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { unwrapData } from 'common/utils/apiResponse';
import { joinTags } from './usePlaceReviews';
import type { StoreSlot } from 'components/place/StorePhotoSlots';
import type { VisitPhotoDraft } from 'components/place/VisitPhotoWrite';

interface PlaceRef {
    placeId: string;
    placeName?: string;
    /** 모임에서 올리는 경우 — 서버가 참여자인지 확인합니다 */
    meetingId?: string;
    /** 여행 N일차 — 기록을 일차별로 묶어 보기 위해 같이 저장합니다 */
    dayNumber?: number;
    /** 어느 일정에서 남긴 기록인지 (meeting_sub.sub_id) */
    subId?: number;
}

/**
 * 장소 사진·후기 올리기.
 *
 * 후기는 글 한 건을 먼저 만들고 그 번호로 사진을 붙입니다.
 * 사진부터 올리면 후기 등록이 실패했을 때 주인 없는 사진이 남습니다.
 */

/** onRequest 응답이 성공인가 — 상태 코드가 없으면(네트워크 오류) 실패로 봅니다 */
const ok2xx = (res: any): boolean => {
    const s = res?.status;
    return typeof s === 'number' && s >= 200 && s < 300;
};

/** 서버가 준 실패 사유. 없으면 undefined */
const serverMessage = (res: any): string | undefined =>
    res?.data?.message || undefined;

export const usePlacePhotoUpload = (place: PlaceRef) => {
    const [busy, setBusy] = useState(false);

    /** 가게 사진 한 자리 — 후기와 무관한 독립 사진입니다 */
    const uploadStorePhoto = useCallback(async (slot: StoreSlot, files: File[]) => {
        const file = files[0];
        if (!file || !place.placeId) return false;
        setBusy(true);
        try {
            await PlaceReviewRepository.uploadAndAddPhoto(place.placeId, file, {
                placeName: place.placeName,
                meetingId: place.meetingId,
                photoKind: 'STORE',
                storeSlot: slot,
            });
            return true;
        } catch {
            return false;
        } finally {
            setBusy(false);
        }
    }, [place.placeId, place.placeName, place.meetingId]);

    /** 방문 사진 한 건 = 후기 하나 + 딸린 사진들 */
    /** 돌려주는 값 — ok 는 후기 등록 성공 여부, photosFailed 는 못 올린 장 수 */
    const submitVisitPost = useCallback(async (draft: VisitPhotoDraft) => {
        if (!place.placeId) {
            /* 장소를 못 정한 채로는 기록을 붙일 데가 없습니다.
               예전에는 이유 없이 실패만 돌려줘 화면이 조용했습니다. */
            return {
                ok: false,
                photosFailed: draft.photos.length,
                message: '기록을 남길 장소를 찾지 못했어요.',
            };
        }
        setBusy(true);
        try {
            /* onRequest 는 에러를 던지지 않고 응답을 그대로 돌려줍니다.
               그래서 아래 try/catch 로는 실패를 잡을 수 없습니다 — 응답을
               직접 봐야 합니다. 예전에는 400 이 와도 ok:true 를 돌려줘서
               "에러도 없고 등록도 안 되는" 상태가 됐습니다. */
            const res = await PlaceReviewRepository.createReview(place.placeId, {
                placeName: place.placeName,
                meetingId: place.meetingId,
                dayNumber: place.dayNumber,
                subId: place.subId,
                /* 별점 0 은 "안 매김" 이라 보내지 않습니다 — 서버가 null 로 받습니다 */
                rating: draft.rating > 0 ? draft.rating : undefined,
                content: draft.content,
                tags: joinTags(draft.tags),
            });
            const reviewId = unwrapData<{ reviewId?: number }>(res)?.reviewId;
            if (!reviewId) {
                return {
                    ok: false,
                    photosFailed: draft.photos.length,
                    message: serverMessage(res),
                };
            }

            /* 사진은 순서를 지켜 올립니다 — 첫 장이 목록의 대표가 됩니다.
               한 장이 실패해도 후기 자체는 이미 등록됐으므로 "등록 실패" 라고
               하면 안 됩니다. 다만 몇 장이 빠졌는지는 돌려줍니다 — 예전에는 전부
               실패해도 그냥 true 를 돌려줘서, 사진이 하나도 안 올라갔는데
               "올렸습니다" 만 뜼고 화면은 비어 있었습니다. */
            let photosFailed = 0;
            for (const file of draft.photos) {
                try {
                    const up = await PlaceReviewRepository.uploadAndAddPhoto(place.placeId, file, {
                        placeName: place.placeName,
                        meetingId: place.meetingId,
                        reviewId,
                        photoKind: 'VISIT',
                    });
                    /* 여기도 throw 가 없습니다. 상태 코드를 직접 봅니다 —
                       그러지 않으면 한 장도 안 올라가도 photosFailed 가 0 입니다. */
                    if (!ok2xx(up)) photosFailed += 1;
                } catch {
                    photosFailed += 1;
                }
            }
            return { ok: true, photosFailed };
        } catch (e: any) {
            return {
                ok: false,
                photosFailed: draft.photos.length,
                message: e?.response?.data?.message,
            };
        } finally {
            setBusy(false);
        }
    }, [place.placeId, place.placeName, place.meetingId, place.dayNumber, place.subId]);

    return { busy, uploadStorePhoto, submitVisitPost };
};
